import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import { Router } from "@angular/router";
import { GuideTitleEnum } from "@bitwarden/web-vault/app/models/enum/guide.enum";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { WizardView } from "@bitwarden/web-vault/app/models/view/wizard/wizard.view";
import {
  GuideWizardService,
  tasks,
} from "@vault/web-vault/app/services/guideWizard/guide-wizard.service";

interface Task {
  id: number;
  title: string;
  reward: number;
  rewardType: string;
  hasRoute: boolean;
}

//the task index is in guide-wizard.service.ts
const premiumOnlyTasks: number[] = [6];
const hideTasks: number[] = [2];

const taskRouteMapping: Record<string, string> = {
  explorePlugin: "plugins",
  viewYourCredits: "credits",
  watchAdsToEarn: "credits",
  addManualAccount: "manage/account",
  fetchAnAccount: "manage/account",
};

// Extra query params per task: "Explore the Plugin Store" opens the Plugins page on its Store tab.
const taskQueryParams: Record<string, Record<string, string>> = {
  explorePlugin: { tab: "store" },
};

@Component({
  selector: "app-guide",
  templateUrl: "./guide.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuideComponent implements OnInit {
  protected router: Router = inject(Router);
  protected userStoreService: UserStoreService = inject(UserStoreService);
  protected guideWizardService: GuideWizardService = inject(GuideWizardService);

  // Computed, not a copy taken at construction: the subscription loads after this component can
  // be created, so a copy would read as not-premium until the guide is opened again.
  protected isPremium = computed(
    () => this.userStoreService.subscription.activeSubscription()?.isPremium ?? false,
  );
  protected inProgressTasks: WritableSignal<Task[]> = signal<Task[]>([]);
  protected completedTasks: WritableSignal<Task[]> = signal<Task[]>([]);
  // hideFilter: when true the tab/filter row is hidden (temporary toggle)
  protected hideFilter: WritableSignal<boolean> = signal(true);
  protected readonly tabs: string[] = ["allTasks", "rewards", "accounts", "plugins"];

  closeDialog = input<CallableFunction | undefined>(undefined);
  isPremiumUser = input<boolean>(false);
  protected hidedTasksIndex: Signal<number[]> = computed(() =>
    this.isPremium() || this.isPremiumUser() ? hideTasks : [...hideTasks, ...premiumOnlyTasks],
  );

  async ngOnInit() {
    const completedTaskIds = this.wizardTasks()
      ?.filter((wizardTask) => wizardTask.step.isComplete)
      .map((wizardTask) => wizardTask.step.stepIndex);
    this.completedTasks.set(tasks.filter((task) => completedTaskIds?.includes(task.id)));
    this.inProgressTasks.set(tasks.filter((task) => !this.completedTasks().includes(task)));
  }

  protected wizardTasks: Signal<WizardView[]> = computed(() => {
    return this.guideWizardService.wizardTasks();
  });

  protected hasLocation = computed(() => {
    const location = this.userStoreService.preferences.preferenceView()?.userLocation;
    return location !== undefined;
  });

  protected allTasksCompleted = computed(() => {
    return this.inProgressTasks().length === 0 && this.completedTasks().length > 0;
  });

  async completeTask(task: Task) {
    // double locked to make sure the task is up to date when completeTask is called, then save it to memory
    this.completedTasks.update((curCompletedTasks) => [...curCompletedTasks, task]);
    this.inProgressTasks.update((inProgress) => inProgress.filter((t) => t !== task));
    await this.guideWizardService.completeTask(task);
  }

  async navigateToTaskPage(task: Task) {
    if (!task.hasRoute) {
      return;
    }
    this.closeDialog()?.();
    await this.router.navigate([taskRouteMapping[task.title]], {
      queryParams: {
        isFromGuide: true,
        guide: GuideTitleEnum[task.title],
        ...taskQueryParams[task.title],
      },
    });
  }

  async goToHome() {
    this.closeDialog()?.();
    await this.router.navigate(["/"]);
  }

  protected isShownTask = (task: Task): boolean => !this.hidedTasksIndex().includes(task.id);
}
